import * as React from 'react';
import { useSelector } from 'react-redux';

import { MortgageOfferRowContainer } from './MortgageOfferRowContainer';
import { MortgageOffersNotFoundContainer } from './MortgageOffersNotFoundContainer';
import {
  MortgageOffersHeaderRow,
  MortgageOffersTable,
  MortgageOffersExpandToggle,
} from '../../../components/NewbuildingMortgageSection';
import { VISIBLE_MORTGAGE_PROMOS_COUNT } from '../../../constants/newbuildingMortgage';
import { selectMortgagePromos } from '../../../selectors/newbuildingMortgageCalculator';
import { IPromoInfo } from '../../../types/newbuildingPromos';
import { filterAndSortMortgageOffers, getMonthlyPayment } from '../helpers';

export interface IMortgageOffersTableContainerProps {
  initialPayment: number;
  loanTerm: number;
  price: number;
  onOffersCountChange: (count: number) => void;
  onDisplayedOffersChange: (promo: IPromoInfo[]) => void;
}

export const MortgageOffersTableContainer: React.FC<React.PropsWithChildren<IMortgageOffersTableContainerProps>> = ({
  initialPayment,
  loanTerm,
  price,
  onOffersCountChange,
  onDisplayedOffersChange,
}) => {
  const promos = useSelector(selectMortgagePromos);

  const filteredPromos = React.useMemo(
    () => filterAndSortMortgageOffers(promos, price, initialPayment, loanTerm),
    [promos, price, initialPayment, loanTerm],
  );

  const [expanded, setExpanded] = React.useState(false);

  const toggleExpanded = React.useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const displayedPromos = expanded ? filteredPromos : filteredPromos.slice(0, VISIBLE_MORTGAGE_PROMOS_COUNT);
  const countToDisplay = filteredPromos.length - VISIBLE_MORTGAGE_PROMOS_COUNT;
  const monthlyPaymentConditions = { loanTerm, price, initialPayment };

  React.useEffect(() => {
    onOffersCountChange(filteredPromos.length);
  }, [filteredPromos.length, onOffersCountChange]);

  React.useEffect(() => {
    onDisplayedOffersChange(displayedPromos);
  }, [displayedPromos, onDisplayedOffersChange]);

  return (
    <MortgageOffersTable>
      {displayedPromos.length > 0 ? (
        <>
          <MortgageOffersHeaderRow />
          {displayedPromos.map((promo, index) => (
            <MortgageOfferRowContainer
              key={promo.uniquePromoId}
              position={index + 1}
              promo={promo}
              monthlyPayment={getMonthlyPayment(promo, monthlyPaymentConditions)}
            />
          ))}
          {countToDisplay > 0 && (
            <MortgageOffersExpandToggle expanded={expanded} count={countToDisplay} onClick={toggleExpanded} />
          )}
        </>
      ) : (
        <MortgageOffersNotFoundContainer />
      )}
    </MortgageOffersTable>
  );
};
